"use client";

import { HomeStore, rootStore } from "@/src/store";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { LOCAL_STORAGE_KEYS, ROUTER_PATH } from "@/src/config";
import {
  OTHERS_SIDE_MENU_NAV,
  PRIMARY_SIDE_MENU_NAV,
} from "./sideMenuNavConfig";
import React, { FC, memo, useEffect } from "react";
import { TabkeyMap, useLoginModalProvider, useShellRouter } from "@/src/hooks";
import { useFitnessScore } from "@/src/hooks/fitness";

import { I18nUtil } from "@utils/i18n";
// LanguageSwitch removed — migrated to Profile page (V3.0.3)
import { ShellLink } from "@/src/components";
import { Logo } from "./Logo";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { GoalMenuIcon, SideMenuNavItem } from "./SideMenuNavItem";
import { SideMenuScoreSection } from "@/src/components/fitness/sidebar/SideMenuScoreSection";
import { TaskCenterBadge } from "./TaskCenterBadge";
import Translations from "@translate/components/Menu/MenuGuide";
import { cn } from "utils/src/cn";
import log from "logbeacon";
import { useChatNotificationBellDot } from "@/src/components/Notification";
import { useDynamicScrollHeight } from "@/src/hooks/useDynamicScrollHeight";
import { usePathname } from "@/src/hooks/usePathname";
import { useSnapshot } from "valtio";
import { useTrackerTabUnreadDot } from "@/src/hooks/tracker/useTrackerTabUnreadDot";

// import { pathnameIncludes } from "@/src/utils/routeNormalizer";

interface MenuContentProps {
  isShowLogo?: boolean;
  onCancel?: () => void;
  selectedPath?: string;
  onPathSelected?: (path: string) => void;
  /** MenuFooter组件的ref，用于动态计算高度 */
  menuFooterRef?: React.RefObject<HTMLDivElement | null>;
  locale?: string;
}

export const MenuGuideMobile: FC<MenuContentProps> = ({ locale }) => {
  const router = useShellRouter();
  const { isOpen } = useSnapshot(HomeStore);
  // const loginSnapshot = useSnapshot(rootStore.login);
  // const userSnapshot = useSnapshot(rootStore.user);
  const I18n = I18nUtil.createScoped(Translations, locale);
  const { open } = useLoginModalProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  // const { data: memberInfo } = useMemberUser();
  // const { credits } = useCredits();
  // const showMembership = loginSnapshot.isLoggedIn && !memberInfo?.isFree;

  const handleUpdateChat = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isLoggedIn) {
      e.stopPropagation();
      e.preventDefault();
      open?.();
      return;
    }
  };

  const menuItemBaseClass =
    "flex w-full items-center justify-start gap-2 rounded-lg py-2";
  const menuItemTextClass = "body_medium_m text-content_secondary";

  // const renderUserInfoBar = () => {
  //   if (!loginSnapshot.isLoggedIn) {
  //     return null;
  //   }
  //   return (
  //     <div className="mb-3 flex items-center gap-2.5">
  //       <AvatarWithFallback
  //         src={userSnapshot.ownerInfo?.avatar}
  //         alt="User Avatar"
  //         width={32}
  //         height={32}
  //         className="rounded-full border border-border_solid"
  //       />
  //       <div className="min-w-0 flex-1">
  //         <div className="flex items-center gap-1">
  //           <span className="body_medium_l max-w-[160px] truncate text-content_primary">
  //             {userSnapshot.ownerInfo?.name || "Edgen User"}
  //           </span>
  //           {showMembership && (
  //             <span className="rounded-[3px] bg-background_brand_overlay100 px-1 body_medium_xs text-content_inverse_primary">
  //               {memberInfo?.name}
  //             </span>
  //           )}
  //         </div>
  //         <div className="body_s text-content_secondary">
  //           {credits || 0} Credits
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const handleReferralClick = () => {
    log.info("[文案]sidebar_referral_click", { ownerInfo });
    const referralPath = `${ROUTER_PATH.AURA_PROFILE}/${ownerInfo?.id}?tabkey=${TabkeyMap.referral}`;
    if (!isLoggedIn) {
      open?.({
        reason: "menu_referral",
        postLoginAction: {
          type: "navigate_dynamic",
          waitForOwnerInfo: true,
          waitTimeoutMs: 3000,
          fallbackTo: ROUTER_PATH.AURA_PROFILE,
          toBuilder: (ctx) => {
            const ownerId = ctx.ownerInfo?.id;
            if (!ownerId) {
              return ROUTER_PATH.ALPHA_PICK;
            }
            return `${ROUTER_PATH.AURA_PROFILE}/${ownerId}?tabkey=${TabkeyMap.referral}`;
          },
        },
      });
      return;
    }
    router.push(referralPath);
  };

  const renderReferral = () => {
    return (
      <div
        className="mx-3 mb-3 flex items-center gap-2 rounded-3 bg-background_white_overlay5 p-3"
        data-openreplay-label="sidebar_referral_click"
        onClick={handleReferralClick}
      >
        <Icon name="ed" size={20} className="text-content_brand_primary" />
        <div className="min-w-0 flex-1">
          <div className="body_medium_m text-content_primary">
            {I18n.t("Share Ed with a friend")}
          </div>
          <div className="body_s text-content_secondary">
            {I18n.t("Each gets")}{" "}
            <span className="text-content_accent4_primary body_medium_s">
              {I18n.t("500 credits")}
            </span>
          </div>
        </div>
        <Icon
          name="right-arrow-alt"
          size={16}
          color={ospThemes.colors.content_tertiary}
        />
      </div>
    );
  };

  return (
    <div
      className={cn("flex-1 flex-shrink-0 flex flex-col", {
        "gap-6": isOpen,
      })}
    >
      <Logo />
      <div className="w-full p-3 flex flex-col gap-2">
        <ShellLink
          title="Menu Guide"
          aria-label="Menu Guide"
          data-openreplay-label={`Menu Guide Link | ${ROUTER_PATH.MEMBER}`}
          id="lnk-menu-guide-subscriptions"
          href={ROUTER_PATH.MEMBER}
          className={menuItemBaseClass}
        >
          <OspIcons
            name="dollar-circle"
            size={16}
            className="text-content_secondary"
          />
          <div className={menuItemTextClass}>{I18n.t("Subscriptions")}</div>
        </ShellLink>
        <ShellLink
          title="Menu Guide"
          aria-label="Menu Guide"
          data-openreplay-label={`Menu Guide Link | ${ROUTER_PATH.DISCOVER_US_STOCKS}`}
          id="lnk-menu-guide-discover"
          href={ROUTER_PATH.DISCOVER_US_STOCKS}
          className={menuItemBaseClass}
        >
          <OspIcons
            name="explore"
            size={16}
            className="text-content_secondary"
          />
          <div className={menuItemTextClass}>{I18n.t("Discover")}</div>
        </ShellLink>
        <ShellLink
          title="Menu Guide"
          aria-label="Menu Guide"
          data-openreplay-label={`Menu Guide Link | ${ROUTER_PATH.AURA_PROFILE}`}
          id="lnk-menu-guide-missions"
          href={
            ownerInfo?.id
              ? `${ROUTER_PATH.AURA_PROFILE}/${ownerInfo.id}`
              : ROUTER_PATH.AURA_PROFILE
          }
          className={menuItemBaseClass}
          onClick={handleUpdateChat}
        >
          <OspIcons
            name="aura_solid"
            size={16}
            className="text-content_secondary"
          />
          <div className={menuItemTextClass}>{I18n.t("Missions")}</div>
        </ShellLink>
        <ShellLink
          title="Menu Guide"
          aria-label="Menu Guide"
          data-openreplay-label={`Menu Guide Link | ${ROUTER_PATH.MY_GOALS}`}
          id="lnk-menu-guide-goal"
          href={ROUTER_PATH.MY_GOALS}
          className={menuItemBaseClass}
        >
          <GoalMenuIcon size={16} className="text-content_secondary" />
          <div className={menuItemTextClass}>{I18n.t("Goal")}</div>
        </ShellLink>
        {/* V3.0.3: Language 入口已迁移至 Profile 页 — 侧边栏不再提供 */}
      </div>
      {renderReferral()}
    </div>
  );
};

// const HomeMeuneItem: React.FC<{
//   title: string;
//   closeTitle?: string;
//   icon: string;
//   focusIcon?: string;
//   href: string;
//   activeHref?: string;
//   onCancel?: () => void;
//   localePrefix: string;
//   trackingScope?: string;
//   suffix?: React.ReactNode;
//   className?: string;
//   showDot?: boolean;
//   /** 自定义点击处理（例如 Aura 权限校验跳转），如果存在则优先执行该逻辑 */
//   onClick?: (event?: React.MouseEvent) => void;
// }> = ({
//   title,
//   showDot,
//   icon,
//   focusIcon,
//   href,
//   onCancel,
//   closeTitle,
//   localePrefix,
//   trackingScope,
//   suffix,
//   activeHref,
//   onClick,
//   className,
// }) => {
//   const pathname = usePathname();
//   const { isOpen } = useSnapshot(HomeStore);

//   let isCurrent = false;
//   if (activeHref) {
//     isCurrent = pathnameIncludes(pathname, activeHref);
//   } else {
//     isCurrent =
//       pathname === href ||
//       (pathname === "/app" && href === ROUTER_PATH.HOME) ||
//       (pathname === `/` && href === ROUTER_PATH.HOME);
//   }

//   return (
//     <ShellLink
//       aria-label="Menu Guide"
//       data-openreplay-label={`Menu Guide Link | ${`${localePrefix}${href}`}`}
//       id="lnk-menu-guide-6"
//       className={cn(
//         "rounded-lg group w-full flex items-center gap-3 p-2 group",
//         {
//           "bg-background_brand_overlay5": isCurrent && isOpen,
//           "hover:bg-background_brand_overlay5": isOpen,
//         },
//         className,
//         {
//           ["justify-center flex-col p-2 gap-1 w-8 h-8 rounded-2"]: !isOpen,
//         },
//       )}
//       href={`${localePrefix}${href}`}
//       onClick={(event) => {
//         // Aura 等场景：传入了自定义 onClick（handleAccessClickAura），内部会自己处理 preventDefault 和跳转
//         if (onClick) {
//           onClick(event);
//           return;
//         }
//         // 普通菜单：仅关闭菜单，交给 Link 按 href 正常跳转
//         onCancel?.();
//       }}
//       data-tracking-scope={trackingScope}
//       // locale={pathnameLocal}
//     >
//       <div
//         className={cn(
//           "w-6 h-6 flex items-center justify-center relative",
//           // {
//           //   "bg-background_brand_overlay100": isCurrent,
//           // },
//         )}
//       >
//         <OspIcons
//           name={isCurrent ? focusIcon || icon : icon}
//           size={18}
//           className={cn(
//             "text-content_quaternary group-hover:text-content_brand_primary",
//             {
//               "text-content_brand_primary group-hover:text-content_brand_primary":
//                 isCurrent,
//             },
//           )}
//         />
//         {showDot && (
//           <span className="absolute -right-1 -top-1 size-[11px] border-[2px] border-background_solid_neutral_tertiary rounded-full bg-content_accent4_primary" />
//         )}
//       </div>
//       <span
//         className={cn(
//           "text-content_primary body_medium_m group-hover:text-content_primary w-fit block",
//           {
//             hidden: !isOpen,
//             "text-content_primary group-hover:text-content_primary": isCurrent,
//           },
//         )}
//       >
//         {isOpen ? title : closeTitle || title}
//       </span>
//       {!!suffix && isOpen && <div>{suffix}</div>}
//     </ShellLink>
//   );
// };

const MenuGuideComponent: FC<MenuContentProps> = ({
  onCancel,
  menuFooterRef,
  locale,
}) => {
  const router = useShellRouter();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const pathname = usePathname();
  const { refetch: refetchBellDot } = useChatNotificationBellDot(ownerInfo?.id);
  const I18n = I18nUtil.createScoped(Translations, locale);
  const localePrefix = locale === "en" ? "" : `/${locale}`;

  const { hasUnreadDot: hasTrackerUnreadDot } = useTrackerTabUnreadDot();
  const { isOpen } = useSnapshot(HomeStore);
  const { data: fitnessScore, isLoading: isFitnessScoreLoading } =
    useFitnessScore();

  // 使用动态高度计算Hook
  const { scrollHeight, scrollContainerRef } = useDynamicScrollHeight({
    fixedHeight: 136, // 固定减去136px
    subtractElementRef: menuFooterRef, // 减去MenuFooter的动态高度
    minHeight: 200, // 最小高度200px
    offset: 12, // 额外偏移量12px
  });

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const isNotificationPage =
      pathname === "/notification" || pathname.startsWith("/notification/");

    if (isNotificationPage) {
      return;
    }

    // 首次加载及后续菜单切换时，主动同步一次通知红点状态。
    void refetchBellDot();
  }, [isLoggedIn, pathname, refetchBellDot]);

  return (
    <div className="flex-1 flex-shrink-0 flex flex-col">
      <Logo />
      {/* {isOpen && (
        <div
          data-openreplay-label="Menu · MenuGuide · div"
          onClick={async () => {
            await MenuSearchModalCreateAble({
              type: "normal",
            });
            onCancel?.();
          }}
          className="w-full bg-background_white_overlay5 rounded-2 border-background_white_overlay5 hover:border-background_white_overlay10 border-solid border-[1px] flex flex-row h-8 cursor-pointer gap-2  items-center px-3 py-2"
        >
          <Icon name="search" size={20} className="text-content_primary" />
          <span className="body_m text-content_quaternary">
            {I18n.t("Search")}
          </span>
        </div>
      )} */}
      <div
        ref={scrollContainerRef}
        className={cn("overflow-y-auto no-scrollbar flex flex-col relative", {
          "gap-6 px-4 py-3": isOpen,
          "px-3": !isOpen,
        })}
        style={{ height: scrollHeight ? `${scrollHeight}px` : "100%" }}
      >
        {isOpen ? (
          <>
            <ShellLink
              className="flex items-center gap-2 py-2 px-3 rounded-2 bg-content_brand_primary group w-fit"
              onClick={() => {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
              }}
              href={ROUTER_PATH.CHAT}
            >
              <OspIcons
                name="ed"
                size={20}
                className="text-content_inverse_primary"
              />
              <div className="body_medium_m group-hover:text-content_inverse_primary text-content_inverse_primary">
                {I18n.t("Chat with Ed")}
              </div>
            </ShellLink>

            {/* Figma 97318:90625 — 点击关闭侧栏并跳转 Financial fitness score 页 */}
            <SideMenuScoreSection
              score={fitnessScore?.value ?? null}
              isLoading={isFitnessScoreLoading}
              onSeeDetails={() => {
                onCancel?.();
                router.push(ROUTER_PATH.MY_SCORE);
              }}
            />

            <div className="flex flex-col gap-3">
              {PRIMARY_SIDE_MENU_NAV().map((item) => (
                <SideMenuNavItem
                  key={item.id}
                  item={item}
                  label={item.label}
                  closeLabel={
                    item.closeLabel ||
                    (item.closeLabelKey
                      ? I18n.t(item.closeLabelKey)
                      : undefined)
                  }
                  href={item.resolveHref({
                    isLoggedIn: !!isLoggedIn,
                    ownerId: ownerInfo?.id,
                  })}
                  localePrefix={localePrefix}
                  onCancel={onCancel}
                  showDot={
                    (item.id === "desk" || item.id === "track") &&
                    hasTrackerUnreadDot
                  }
                />
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <span className="body_s text-content_quaternary px-2">
                {I18n.t("Others")}
              </span>
              <div className="flex flex-col">
                {OTHERS_SIDE_MENU_NAV().map((item) => (
                  <SideMenuNavItem
                    key={item.id}
                    item={item}
                    label={item.label}
                    closeLabel={
                      item.closeLabel ||
                      (item.closeLabelKey
                        ? I18n.t(item.closeLabelKey)
                        : undefined)
                    }
                    href={item.resolveHref({
                      isLoggedIn: !!isLoggedIn,
                      ownerId: ownerInfo?.id,
                    })}
                    showDot={
                      (item.id === "desk" || item.id === "track") &&
                      hasTrackerUnreadDot
                    }
                    localePrefix={localePrefix}
                    onCancel={onCancel}
                    suffix={
                      item.action === "missions" ? (
                        <TaskCenterBadge className="md:ml-2" />
                      ) : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-3 flex flex-col items-center gap-[28px]">
            <ShellLink
              href={ROUTER_PATH.CHAT}
              className="block"
              onClick={() => {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
              }}
            >
              <div className="p-1 rounded-[6px] bg-background_brand_overlay100 w-8 h-8 mx-auto">
                <OspIcons
                  name="ed"
                  size={24}
                  className="text-content_inverse_primary"
                />
              </div>
              <div className="body_bold_xs text-content_brand_primary text-center mt-1 mb-0.5">
                {I18n.t("Chat")}
              </div>
            </ShellLink>

            {PRIMARY_SIDE_MENU_NAV().map((item) => (
              <SideMenuNavItem
                key={item.id}
                item={item}
                label={item.label}
                closeLabel={
                  item.closeLabel ||
                  (item.closeLabelKey ? I18n.t(item.closeLabelKey) : undefined)
                }
                href={item.resolveHref({
                  isLoggedIn: !!isLoggedIn,
                  ownerId: ownerInfo?.id,
                })}
                localePrefix={localePrefix}
                onCancel={onCancel}
              />
            ))}

            {OTHERS_SIDE_MENU_NAV().map((item) => (
              <SideMenuNavItem
                key={item.id}
                item={item}
                label={item.label}
                closeLabel={
                  item.closeLabel ||
                  (item.closeLabelKey ? I18n.t(item.closeLabelKey) : undefined)
                }
                href={item.resolveHref({
                  isLoggedIn: !!isLoggedIn,
                  ownerId: ownerInfo?.id,
                })}
                localePrefix={localePrefix}
                onCancel={onCancel}
                showDot={
                  (item.id === "desk" || item.id === "track") &&
                  hasTrackerUnreadDot
                }
                suffix={
                  item.action === "missions" ? (
                    <TaskCenterBadge className="absolute right-4 top-1.5" />
                  ) : undefined
                }
              />
            ))}
          </div>
        )}
        {/* <HomeMeuneItem
            title={I18n.t("Chat History")}
            closeTitle={I18n.t("History")}
            icon="time-five"
            activeHref={ROUTER_PATH.CHAT_HISTORY}
            href={ROUTER_PATH.CHAT_HISTORY}
            onCancel={onCancel}
            localePrefix={localePrefix}
            trackingScope="menu-guide-chat-history"
          /> */}
      </div>
    </div>
  );
};

// 使用React.memo优化组件，提供自定义比较函数
export const MenuGuide = memo(MenuGuideComponent, (prevProps, nextProps) => {
  return (
    prevProps.onCancel === nextProps.onCancel &&
    prevProps.selectedPath === nextProps.selectedPath &&
    prevProps.onPathSelected === nextProps.onPathSelected &&
    prevProps.isShowLogo === nextProps.isShowLogo
  );
});
